import { useEffect, useState } from "react";

import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import FilePondPluginImageResize from "filepond-plugin-image-resize";
import { FilePond, registerPlugin } from "react-filepond";

import { Box } from "@mui/material";
import { AlertCircle, Repeat, XCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { TailSpin } from "react-loader-spinner";

import { ADD_PAIEMENT, LISTE_OPERATEUR } from "../../Utils/constant";
import { store } from "../../services/service";
import { useGlobalStore } from "../../store/store";
import { useVerifyPaiementStore } from "../../store/verifyPaiementStore";
import PaymentValidationWaiting from "../Widget/EnAttentPaiement";
import { SubmitButton } from "../Widget/SubmitButton";
import { ValidateMessage } from "../Widget/ValidateMessage";

import cash from "../../assets/images/cash.png";
import moov from "../../assets/images/moov-money.png";
import momo from "../../assets/images/mtn-mooney.png";
import orange from "../../assets/images/om.png";
import visa from "../../assets/images/visa.png";
import wave from "../../assets/images/wave.png";

registerPlugin(
  FilePondPluginImageExifOrientation,
  FilePondPluginImagePreview,
  FilePondPluginImageResize,
);

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAYMENT_IMAGES = [
  { code: "WAVE_SKAN", image: wave },
  { code: "OM_SKAN", image: orange },
  { code: "MOOV_SKAN", image: moov },
  { code: "MOMO", image: momo },
  { code: "CARTE_BANCAIRE_CINETPAY_SKAN", image: visa },
  { code: "CASH", image: cash },
];

const SCAN_PROVIDERS = ["WAVE_SKAN", "CARTE_BANCAIRE_CINETPAY_SKAN"];
const POLLING_INTERVAL_MS = 2000;

const getProviderImage = (code) =>
  PAYMENT_IMAGES.find((p) => p.code === code)?.image;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function PaymentError({ message, onRetry }) {
  return (
    <div className="bg-white rounded-2xl border p-6 mb-6">
      <div className="text-center">
        <div className="w-24 h-24 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
          <XCircle className="w-12 h-12 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-red-600 mb-2">
          Échec du paiement
        </h1>
        <p className="text-gray-600 mb-4">
          Votre transaction n&apos;a pas pu être finalisée
        </p>
        <p className="text-red-500 mb-4">
          {message === "null" ? "Transaction rejetée" : message}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center px-4 py-2 bg-red-100 text-red-800 rounded-full text-sm font-medium cursor-pointer"
        >
          <Repeat className="w-4 h-4 mr-2" />
          Réessayer
        </button>
      </div>
    </div>
  );
}

function NoPboAvailable({ message }) {
  return (
    <div className="bg-white rounded-2xl border p-6 mb-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <AlertCircle className="w-6 h-6 text-red-500" />
        </div>
        <h1 className="text-xl font-bold text-red-500 mb-2 font-mtn">
          Aucun PBO disponible
        </h1>
        <p className="text-gray-600 mb-4">
          {message ??
            "Désolé, il n'y a plus de brin disponible pour cette intention"}
        </p>
      </div>
    </div>
  );
}

function ProviderGrid({ providers, selectedCode, onSelect }) {
  return (
    <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
      {providers.map((provider) => (
        <button
          key={provider.id}
          type="button"
          onClick={() => onSelect(provider.code)}
          className={`cursor-pointer px-2 py-1 flex items-center justify-center rounded-xl border transition-all duration-200 hover:scale-105 ${
            selectedCode === provider.code
              ? "border-2 border-black"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <div className="flex flex-col items-center justify-center gap-3">
            <img
              className="rounded-full h-8 w-8 object-cover"
              src={getProviderImage(provider.code)}
              alt={provider.designation}
            />
            <p className="font-medium text-xs">{provider.designation}</p>
          </div>
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const CollectePaiement = ({ handleNext, numeroTicket }) => {
  const typeClient = localStorage.getItem("typeClient");
  const [filesRecuPaiement, setFilesRecuPaiement] = useState([]);
  const [paiementChoix, setPaiementChoix] = useState(
    typeClient === "ENTREPRISE_POSTPAID" ? "CASH" : "MOMO",
  );
  const [paiementRef, setPaiementRef] = useState(null);
  const [paiementError, setPaiementError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const verifyStore = useVerifyPaiementStore();
  const globalStore = useGlobalStore();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  // ── Initial data fetch ──────────────────────────────────────────────────
  useEffect(() => {
    verifyStore.checking(numeroTicket);
    globalStore.operateurs(LISTE_OPERATEUR);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numeroTicket]);

  // ── Poll payment status ─────────────────────────────────────────────────
  useEffect(() => {
    if (!paiementRef) return;

    const titre = verifyStore?.data?.donnee?.titre;

    if (titre === "ATTENTE_DE_VALIDATION" || verifyStore.loading) {
      const interval = setInterval(() => {
        verifyStore.verification(paiementRef);
      }, POLLING_INTERVAL_MS);
      return () => clearInterval(interval);
    }

    if (titre === "SUCCES") {
      setIsSubmitting(false);
    } else if (titre === "ECHEC") {
      setIsSubmitting(false);
      setPaiementError(verifyStore?.data?.donnee?.message ?? "Erreur inconnue");
    }
  }, [paiementRef, verifyStore]);

  // ── Derived state ───────────────────────────────────────────────────────
  const paymentStatus = verifyStore?.data?.donnee?.titre;
  const isFreeData = verifyStore?.isFree;
  const isLoading = verifyStore?.isloading && isFreeData === null;
  const hasError = isFreeData === "error";

  const isPaymentSuccess =
    (paymentStatus === "SUCCES" && paiementRef !== null) ||
    Boolean(isFreeData?.donnee);

  const isWaitingForValidation =
    paiementChoix !== "CASH" &&
    (paymentStatus === "ATTENTE_DE_VALIDATION" ||
      (verifyStore?.data === null && verifyStore?.loading)) &&
    paiementRef !== null;

  const activeProviders =
    globalStore?.operateur?.donnee?.filter((p) => p.typeEtat === "ACTIF") ?? [];

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleUpdateRecu = (fileItems) => {
    setFilesRecuPaiement(fileItems.map((fi) => fi.file));
  };

  const resetPayment = () => {
    setPaiementRef(null);
    setPaiementError(null);
    setIsSubmitting(false);
  };

  const submit = (data) => {
    setIsSubmitting(true);
    setPaiementError(null);

    const requestData = {
      ...data,
      numeroTicket,
      recuPaiement: filesRecuPaiement[0],
      typePaiement: "PAIEMENT_EQUIPEMENT",
      codeOperateur: paiementChoix === "CASH" ? "" : paiementChoix,
    };

    store(ADD_PAIEMENT, "multipart/form-data", requestData)
      .then((res) => {
        const { reference, titre, message } = res.data.donnee;

        setPaiementRef(reference);

        if (SCAN_PROVIDERS.includes(paiementChoix)) {
          window.open(message, "_blank", "noopener,noreferrer");
        }

        if (titre === "ECHEC" || titre === "ECHEC_PAIEMENT") {
          setIsSubmitting(false);
          setPaiementError(message);
        } else {
          verifyStore.verification(reference);
        }
      })
      .catch((err) => {
        setIsSubmitting(false);
        toast.error(err?.response?.data?.donnee ?? "Une erreur est survenue");
      });
  };

  // ── Can continue to next step ────────────────────────────────────────────
  const canContinue =
    isPaymentSuccess || (paiementRef !== null && paiementChoix === "CASH");

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="w-full flex flex-col mt-6">
      {/* Loading */}
      {isLoading ? (
        <div className="w-full my-4 flex items-center justify-center">
          <TailSpin
            height="40"
            width="40"
            color="#000"
            ariaLabel="tail-spin-loading"
            radius="2"
            visible
          />
        </div>
      ) : /* Erreur PBO */
      hasError ? (
        <NoPboAvailable message={verifyStore?.msg} />
      ) : (
        /* Contenu principal */
        <>
          {isPaymentSuccess ? (
            <ValidateMessage texte="Paiement validé" />
          ) : isWaitingForValidation ? (
            <PaymentValidationWaiting
              handle={resetPayment}
              image={getProviderImage(paiementChoix)}
            />
          ) : paiementError ? (
            <PaymentError message={paiementError} onRetry={resetPayment} />
          ) : (
            <form
              onSubmit={handleSubmit(submit)}
              className="shadow-sm border rounded w-full p-8"
            >
              {/* Titre */}
              <h1 className="text-amber-500 font-mtn text-2xl font-semibold tracking-tight mb-1">
                Collecte de paiement
              </h1>
              {typeClient !== "ENTREPRISE_POSTPAID" ? (
                <>
                  <p className="text-sm my-3">
                    Veuillez choisir un moyen de paiement et suivre les
                    instructions associées
                  </p>

                  <ProviderGrid
                    providers={activeProviders}
                    selectedCode={paiementChoix}
                    onSelect={setPaiementChoix}
                  />
                </>
              ) : (
                <>
                  <p className="text-sm my-3">
                    Veuillez charger l'evidence de paiement pour l'intention
                    entreprise postpaid
                  </p>
                </>
              )}

              {/* Champs selon le mode de paiement */}
              {paiementChoix === "CASH" ? (
                <label className="form-control w-full mt-4">
                  <span className="label-text mb-1 block">
                    Télécharger le reçu
                  </span>
                  <FilePond
                    files={filesRecuPaiement}
                    onupdatefiles={handleUpdateRecu}
                    allowImageResize
                    acceptedFileTypes={["application/pdf"]}
                    allowMultiple={false}
                    labelIdle="Importer reçu de paiement"
                  />
                </label>
              ) : (
                <>
                  {paiementChoix !== "CARTE_BANCAIRE_CINETPAY_SKAN" && (
                    <label className="form-control w-full mt-4">
                      <div className="label -mb-1">
                        <span className="label-text font-medium">
                          Numéro de téléphone{" "}
                          <span className="text-red-500">*</span>
                        </span>
                      </div>
                      <input
                        {...register("numeroMtn", { required: true })}
                        type="tel"
                        placeholder="05 000 000 00"
                        className={`input input-bordered text-gray-500 w-full h-10 text-sm font-semibold ${
                          errors.numeroMtn ? "input-error" : ""
                        }`}
                      />
                      {errors.numeroMtn && (
                        <p className="text-red-500 text-xs mt-1">
                          Ce champ est requis
                        </p>
                      )}
                    </label>
                  )}

                  {paiementChoix === "OM_SKAN" && (
                    <label className="form-control w-full mt-4">
                      <div className="label -mb-1">
                        <span className="label-text font-medium">
                          Code de sécurité{" "}
                          <span className="text-red-500">*</span>
                        </span>
                      </div>
                      <input
                        {...register("token", { required: true })}
                        type="text"
                        placeholder="Tapez #144*82# pour obtenir un code"
                        className={`input input-bordered text-gray-500 w-full h-10 text-sm font-semibold ${
                          errors.token ? "input-error" : ""
                        }`}
                      />
                      {errors.token && (
                        <p className="text-red-500 text-xs mt-1">
                          Ce champ est requis
                        </p>
                      )}
                    </label>
                  )}
                </>
              )}
              {paiementChoix === "CASH" &&
              filesRecuPaiement.length === 0 ? null : (
                <div className="mt-4">
                  <SubmitButton
                    isSubmit={isSubmitting}
                    label="Faire le paiement"
                  />
                </div>
              )}
            </form>
          )}
        </>
      )}

      {/* Pied de page – bouton Poursuivre */}
      <div className="w-full mt-4">
        <hr />
        <div className="flex mt-4">
          <Box sx={{ flex: "1 1 auto" }} />
          <button
            type="button"
            disabled={!canContinue}
            onClick={handleNext}
            className={`btn btn-sm py-1 px-2 font-medium rounded-lg ${
              canContinue
                ? "bg-[#ffcb05] cursor-pointer"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            Poursuivre
          </button>
        </div>
      </div>
    </div>
  );
};
